import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import axios from "axios";
import produce from "immer";
import { useLocation } from "@gatsbyjs/reach-router";

import StoreContext, { defaultStoreContext, INFLUENCER_DATA_COOKIE } from "./store";
import { GeoContext } from "./GeoProvider";
import { createCookie, getAllUrlParams, removeQueryParam } from "@helpers/browser";
import {
  isStandardShipping,
  getShippingFeeFromCountry,
  getFreeShippingThresholdFromCountry,
} from "@helpers/price";
import { getShopifyObjectId } from "@helpers/product";
import { useALError } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { getAlgoliaIndexBaseKey } from "../../locale-shopifies";
import { useCurrentLang } from "../hooks/useLocalizedCartInfo";
import useDiscountsInfo from "../hooks/useDiscountsInfo";
import DISCOUNT_TYPES from "../constants/DiscountTypes";

function Provider({
  children,
  setDisplayQuickAccess,
  setDisplayAB,
  displayAB,
  hideOnScrollLogo,
  setDisplayNavigation,
  displayNavigation,
  setOptionalProductsData,
  recommendedProductsPopupData,
  setRecommendedProductsPopupData,
  influencersData,
}) {
  const location = useLocation();
  const { sendReport } = useALError();
  const discountsInfo = useDiscountsInfo();
  const { gePriceDetails, isLoadingCountry } = useContext(GeoContext);

  const [store, updateStore] = useState(defaultStoreContext);
  const countryCode = gePriceDetails?.CountryCode;
  const lang = useCurrentLang();

  const { shippingFees, searchIndex } = useMemo(() => {
    if (isLoadingCountry || !countryCode) return {};
    const shippingFees = {
      shippingPrice: getShippingFeeFromCountry(gePriceDetails),
      freeShippingThreshold: getFreeShippingThresholdFromCountry(gePriceDetails),
      isStandardShipping: isStandardShipping(gePriceDetails),
    };
    const algoliaIndex = getAlgoliaIndexBaseKey(countryCode, lang);

    return { shippingFees, searchIndex: `products-${algoliaIndex}` };
  }, [countryCode, gePriceDetails, lang, isLoadingCountry]);

  const giftDiscount = discountsInfo?.find(
    (discount) => discount.type === DISCOUNT_TYPES.FREE_GIFT
  );
  useEffect(() => {
    if (isLoadingCountry) return;

    // TODO:
    // - Add the discount logic to the discount provider
    // - Then remove this useEffect
    if (giftDiscount?.freegift && !giftDiscount?.freegift?.data) {
      const giftHandle = giftDiscount?.freegift?.product.handle;
      axios
        .get(
          `/.netlify/functions/get-product?handles=${giftHandle}&countryCode=${countryCode}&length=short`
        )
        .then((r) => {
          if (r.status === 200) {
            updateStore((state) => ({
              ...state,
              discountInfo: {
                ...state.discountInfo,
                freegift: {
                  ...state.giftDiscount.freegift,
                  data: r.data.products[0],
                },
              },
            }));
          }
        })
        .catch((err) => {
          console.error("error free gift:");
          console.error(err);
          sendReport(err, { name: "Provider", priority: "P1" });
        });
    }
  }, [countryCode, isLoadingCountry, sendReport, giftDiscount?.freegift]);

  const addTrackCartProduct = (trackingCartProduct) => {
    updateStore(
      produce((draftStore) => {
        // We track only one product added by origin
        const isAlreadyTracked = draftStore.trackCartProduct.find(
          (productTracked) =>
            productTracked.productId === trackingCartProduct.productId &&
            productTracked.origin === trackingCartProduct.origin
        );
        if (!isAlreadyTracked) {
          draftStore.trackCartProduct.push(trackingCartProduct);
        }
      })
    );
  };

  const updateSalesColor = (data) => {
    updateStore((store) => ({
      ...store,
      saleColors: data,
    }));
  };

  const setInfluencerDataProvider = useCallback((data) => {
    createCookie(INFLUENCER_DATA_COOKIE, encodeURIComponent(JSON.stringify(data)), 2);
    updateStore((_store) => ({
      ..._store,
      influencerData: data,
    }));
  }, []);

  const fullStore = useMemo(
    () => ({
      ...store,
      shippingFees,
      searchIndex,
    }),
    [store, shippingFees, searchIndex]
  );

  useMemo(() => {
    const queryParams = getAllUrlParams(location.href);
    if (queryParams && queryParams.tag && queryParams.cid && queryParams.email) {
      // Set new date format: yyyy-mm-dd
      const date = new Date();
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();

      const data = JSON.stringify({
        eventName: "tag_shopify",
        email: `${queryParams.email.replaceAll(" ", "+")}`,
        data: {
          tag: queryParams.tag,
          date: `${year}-${month < 10 ? `0${month}` : month}-${day < 10 ? `0${day}` : day}`,
          cid: queryParams.cid,
        },
      });

      const config = {
        method: "GET",
        url: "/.netlify/functions/webhook-bloomreach",
        headers: {
          "Content-Type": "application/json",
        },
        data,
      };

      axios.request(config).catch((error) => {
        sendReport(error, { name: "Provider", priority: "P2" });
      });

      removeQueryParam("tag");
      removeQueryParam("cid");
      removeQueryParam("email");
    }
  }, [location, sendReport]);

  const providerData = useMemo(
    () => ({
      store: fullStore,
      setDisplayQuickAccess,
      addTrackCartProduct,
      getShopifyObjectId,
      updateSalesColor,
      displayAB,
      setDisplayAB,
      setDisplayNavigation,
      displayNavigation,
      setOptionalProductsData,
      recommendedProductsPopupData,
      setRecommendedProductsPopupData,
      hideOnScrollLogo,
      influencersData,
      setInfluencerDataProvider,
    }),
    [
      fullStore,
      setDisplayQuickAccess,
      displayAB,
      setDisplayAB,
      setDisplayNavigation,
      displayNavigation,
      setOptionalProductsData,
      recommendedProductsPopupData,
      setRecommendedProductsPopupData,
      hideOnScrollLogo,
      influencersData,
      setInfluencerDataProvider,
    ]
  );

  return <StoreContext.Provider value={providerData}>{children}</StoreContext.Provider>;
}

export default Provider;
